import React, {useState} from 'react';

import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

import {ListEventSubscriptions} from './__generated/ListEventSubscriptions';
import {isAppSubscription, isEventBridgeSubscription} from './EventsConstants';
import * as EventSubscriptionActions from './EventSubscriptionActions';
import {trackDocumentationClick} from './EventSubscriptionsPage';
import EventTypesSection from './EventTypesSection';

type EventSubscription = ListEventSubscriptions['eventSubscriptionsListing']['eventSubscriptions'][number];

interface Props {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  eventSubscription: EventSubscription;
  expired: Boolean;
}

export default function UpdateEventSubscriptionModal(props: Props): JSX.Element {
  const [eventTypes, setEventTypes] = useState(props.eventSubscription.eventTypes);

  async function handleSubmit(): Promise<void> {
    if (isAppSubscription(props.eventSubscription)) {
      return;
    }
    await EventSubscriptionActions.update(props.eventSubscription.id, {
      eventTypes,
      paused:
        props.expired || isAppSubscription(props.eventSubscription) ? false : props.eventSubscription.paused,
    });
    notifications.success(`Successfully updated event subscription *${props.eventSubscription.name}*`);
    props.onRequestSubmit();
  }

  const bannerContent = (
    <span>
      This subscription expired because no corresponding event bus was set up in AWS within two weeks.
      Resubscribing will create a new partner event source in your AWS account. The{' '}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://docs.benchling.com/docs/events-getting-started#receiving-events"
        onClick={() => {
          trackDocumentationClick('receiving-events');
        }}
      >
        documentation
      </a>{' '}
      includes detailed instructions to complete AWS EventBridge setup.
    </span>
  );

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      title={props.expired ? 'Resubscribe to expired subscription' : 'Update Subscription'}
      buttons={[
        {
          text: props.expired ? 'Resubscribe' : 'Update',
          submit: true,
          style: ButtonStyle.SECONDARY,
          dataTestAttrs: {component: 'EventSubscriptionUpdateModal', element: 'updateButton'},
        },
      ]}
    >
      <div className="u-small-spacing-bottom">
        {props.expired && (
          <div className="u-small-spacing-bottom">
            <InlineBanner bannerType={InlineBannerType.WARNING} text={bannerContent} />
          </div>
        )}
        <div>
          <FieldLabel label="Subscription:" />
          <code>{props.eventSubscription.name}</code>
        </div>
        {isEventBridgeSubscription(props.eventSubscription) && (
          <>
            <div>
              <FieldLabel label="AWS Account:" />
              <code>{props.eventSubscription.awsAccountId}</code>
            </div>
            <div>
              <FieldLabel label="AWS Region:" />
              <code>{props.eventSubscription.awsRegion}</code>
            </div>
          </>
        )}
      </div>
      <EventTypesSection eventTypes={eventTypes} onChangeEventTypes={setEventTypes} />
    </Modal>
  );
}
